/* eslint-disable jsx-a11y/media-has-caption */
import React from 'react';
import Hls from 'hls.js';
import styled from 'styled-components';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

type HlsVideoProps = {
  children?: any;
  styleText: string;
  className: string;
  properties?: {
    src?: string;
    autoplay?: boolean;
    muted?: boolean;
    controls?: boolean;
    loop?: boolean;
    poster?: string;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const HlsVideo = (componentProps: HlsVideoProps) => {
  const parentContext = React.useContext(DataElementContext);
  const [props] = processComponentProps(componentProps, parentContext);
  const { properties, className, styleText } = props as HlsVideoProps;
  const { src, autoplay, muted, loop, poster, ...restProperties } = properties ?? {};
  const videoSrc = src ?? '';

  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [hovered, setHovered] = React.useState(false);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoSrc) return;

    if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(videoSrc);
      hls.attachMedia(video);
      hls.once(Hls.Events.MANIFEST_PARSED, () => {
        const maxLevel = hls.levels.length - 1;
        hls.currentLevel = maxLevel; // start at highest available quality (1080p or whatever CDN has)
        hls.currentLevel = -1;       // re-enable ABR so it can downscale if bandwidth is insufficient
      });
      return () => hls.destroy();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = videoSrc;
    }
  }, [videoSrc]);

  return (
    <DataElementContext.Provider value={parentContext}>
      <ModuleElementDiv
        className={className ?? ''}
        $styleText={styleText}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <video
          ref={videoRef}
          autoPlay
          muted
          loop={loop ?? false}
          controls={hovered}
          poster={poster}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </ModuleElementDiv>
    </DataElementContext.Provider>
  );
};

export default HlsVideo;
